<template>
  <div class="offline-diagnosis-results">
    <!-- 模块 1：离线告警研判核心看板 (Summary Hero Card) -->
    <div class="summary-hero-card mb-4 pa-5 bg-white rounded-xl card-shadow">
      <!-- 第 1 层：告警大标题 (左) + 研判结论徽章 (右) -->
      <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-3">
        <div class="d-flex align-center flex-wrap">
          <h2 class="alarm-main-title text-slate-900 mr-3 mb-0">
            {{ targetAlarmName }}
          </h2>
          <span class="code-badge font-mono mr-2">{{ targetAlarmCode }}</span>
          <span v-if="targetAlarmCategory" class="category-pill mr-2" :class="getCategoryClass(targetAlarmCategory)">
            <v-icon size="13" class="mr-1">{{ getCategoryIcon(targetAlarmCategory) }}</v-icon>
            <span>{{ getCategoryLabel(targetAlarmCategory) }}</span>
          </span>
        </div>

        <div class="d-flex align-center flex-wrap ga-2">
          <span class="verdict-banner-pill tag-triggered">
            <v-icon size="16" class="icon-inline">mdi-lan-disconnect</v-icon>
            {{ summaryTitleSuffix }}
          </span>
        </div>
      </div>

      <!-- 第 2 层：实体三要素 (所属电站、设备UUID、告警发生时刻) - 宽敞舒适，不拥挤，无多余窗口标签 -->
      <div class="alarm-meta-strip d-flex align-center flex-wrap mb-4 text-caption ga-y-2">
        <div class="prop-item mr-8 d-inline-flex align-center">
          <v-icon size="14" color="#64748b" class="icon-inline mr-1">mdi-office-building-outline</v-icon>
          <span class="text-slate-400 mr-1">所属电站:</span>
          <span class="text-slate-800 font-weight-medium">{{ targetStationName }}</span>
        </div>
        <div class="prop-item mr-8 d-inline-flex align-center">
          <v-icon size="14" color="#64748b" class="icon-inline mr-1">mdi-developer-board</v-icon>
          <span class="text-slate-400 mr-1">设备名称:</span>
          <span class="text-slate-800 font-weight-medium">{{ targetDeviceName }}</span>
          <span v-if="targetDeviceUuid" class="text-slate-400 font-mono ml-1">({{ targetDeviceUuid }})</span>
        </div>
        <div class="prop-item d-inline-flex align-center">
          <v-icon size="14" color="#64748b" class="icon-inline mr-1">mdi-clock-outline</v-icon>
          <span class="text-slate-400 mr-1">告警发生时刻:</span>
          <span class="font-mono text-slate-800 font-weight-medium">{{ targetAlarmTime }}</span>
        </div>
      </div>

      <!-- 第 3 层：智能归因综合研判结论 (充足内边距与留白，标题独立居顶，多条结论从 1 到 N 编号，绝不顶线) -->
      <div v-if="diagnosisSummary" class="verdict-highlight-banner">
        <div class="verdict-banner-header d-flex align-center mb-3">
          <span class="verdict-banner-badge mr-2">
            <v-icon size="14" color="#1d4ed8" class="mr-1">mdi-text-box-search-outline</v-icon>
            智能归因研判结论
          </span>
        </div>

        <!-- 结论内容展示区：单条时直接陈述，多条时从 1 到 N 严格有序展开 -->
        <div v-if="conclusionsList.length <= 1"
          class="verdict-banner-text text-body-2 text-slate-800 font-weight-medium">
          {{ conclusionsList[0] || diagnosisSummary }}
        </div>

        <div v-else class="aligned-flow-list">
          <div v-for="(item, idx) in conclusionsList" :key="idx"
            class="flow-item d-flex align-start mb-2 text-body-2 text-slate-800"
            :class="{ 'mb-0': idx === conclusionsList.length - 1 }">
            <span class="step-badge">{{ idx + 1 }}</span>
            <span class="step-text">{{ item }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块 2：多维运行数据核验 -->
    <div class="fact-verification-card mb-4 pa-5 bg-white rounded-xl card-shadow">
      <div class="d-flex align-center justify-space-between mb-4 pb-2 border-b-subtle">
        <div class="d-flex align-center">
          <v-icon size="20" color="#2563eb" class="mr-2">mdi-clipboard-check-outline</v-icon>
          <h3 class="card-title text-slate-800 text-subtitle-1 font-weight-bold mb-0">多维运行数据核验</h3>
        </div>
      </div>

      <!-- 2.1 告警基础频次 -->
      <div v-if="categorizedFacts.basicStats.length" class="mb-3">
        <div class="d-flex align-center mb-1 section-sub-title">
          <v-icon size="16" color="#2563eb" class="mr-1">mdi-chart-bell-curve-cumulative</v-icon>
          <span>告警频次与触发特征</span>
        </div>
        <ul class="fact-list pl-5">
          <li v-for="(fact, fIdx) in categorizedFacts.basicStats" :key="fIdx">
            {{ fact }}
          </li>
        </ul>
      </div>

      <!-- 2.2 上游网络与关键诱因排查 -->
      <div v-if="categorizedFacts.upstreamFact" class="evidence-upstream-box mb-3 pt-3 border-t-subtle">
        <div class="d-flex align-center mb-1 section-sub-title">
          <v-icon size="16" color="#c026d3" class="mr-1">mdi-source-branch</v-icon>
          <span>上游网络与关键诱因排查</span>
          <span class="badge-sub ml-2 text-indigo-700 bg-indigo-50">关联溯源</span>
        </div>
        <p class="text-body-2 text-slate-700 mb-0 pl-5">{{ categorizedFacts.upstreamFact }}</p>
      </div>

      <!-- 2.3 空间拓扑扩散与协同挂账卡片 -->
      <OfflineSpatialTopologyCard :spatial-fact="categorizedFacts.spatialFact"
        :concurrent-devices-fact="categorizedFacts.concurrentDevicesFact" :alarms-list="currentStationAlarms"
        :alarm-record="alarmRecord" />

      <!-- 2.4 近 30 天历史画像卡片 -->
      <OfflineHistoryAnalysisCard :history-facts="categorizedFacts.historyFacts" :history-list="historyAlarms"
        :alarm-record="alarmRecord" />
    </div>

    <!-- 模块 3：设备充放电运行趋势与数据连续性 -->
    <div class="telemetry-evidence-card mb-4 pa-5 bg-white rounded-xl card-shadow">
      <div class="d-flex align-center justify-space-between mb-3 pb-2 border-b-subtle">
        <div class="d-flex align-center">
          <v-icon size="20" color="#2563eb" class="mr-2">mdi-chart-timeline-variant</v-icon>
          <h3 class="card-title text-slate-800 text-subtitle-1 font-weight-bold mb-0">设备充放电运行趋势与数据连续性</h3>
        </div>
      </div>

      <OfflineTelemetryChartCard :records="telemetryRecords" :alarm-record="alarmRecord"
        :raw-fact-text="categorizedFacts.telemetryFact" :power-tracking-start="powerTrackingStart"
        :power-tracking-end="powerTrackingEnd" />
    </div>

    <!-- 模块 4：通信中断原因分析与风险评估 -->
    <div class="action-card bg-white rounded-xl pa-5 card-shadow mb-4">
      <div class="d-flex align-center justify-space-between mb-3 pb-2 border-b-subtle">
        <div class="d-flex align-center">
          <v-icon color="#4f46e5" size="20" class="icon-inline">mdi-code-braces</v-icon>
          <h3 class="card-title text-subtitle-1 font-weight-bold text-slate-800 mb-0">通信中断原因分析与风险评估</h3>
        </div>
      </div>

      <!-- 4.1 硬件供电与通信链路机理 -->
      <div v-if="currentCodeMechanism.length" class="mechanism-box mb-4 rounded-lg">
        <div class="d-flex align-center mb-1 section-sub-title">
          <v-icon size="16" color="#4f46e5" class="mr-1">mdi-chip</v-icon>
          <span>硬件供电与通信链路机理</span>
        </div>
        <p v-for="(mech, idx) in currentCodeMechanism" :key="idx"
          class="mechanism-text mb-1 text-body-2 text-slate-700 pl-5">
          {{ mech }}
        </p>
      </div>

      <!-- 4.2 可能原因排查建议 -->
      <div v-if="currentPossibleCauses.length" class="mb-4">
        <div class="d-flex align-center mb-2">
          <v-icon size="16" color="#2563eb" class="icon-inline">mdi-help-circle-outline</v-icon>
          <h5 class="section-sub-title mb-0">可能原因排查建议</h5>
        </div>
        <div class="aligned-flow-list pl-5">
          <div v-for="(cause, cIdx) in currentPossibleCauses" :key="cIdx"
            class="flow-item d-flex align-start mb-2 text-body-2 text-slate-700">
            <span class="confidence-tag mr-2" :class="`confidence-${String(cause.confidence).toLowerCase()}`">
              {{ confidenceText(cause.confidence) }}
            </span>
            <span class="step-text">{{ cause.cause }}</span>
          </div>
        </div>
      </div>

      <!-- 4.3 可能造成的影响与风险 (素雅克制，杜绝通篇大红) -->
      <div v-if="currentImpact.length" class="pt-3">
        <div class="d-flex align-center mb-2">
          <v-icon size="16" color="#dc2626" class="icon-inline mr-1">mdi-alert-circle-outline</v-icon>
          <h5 class="section-sub-title mb-0">可能造成的影响与风险</h5>
        </div>
        <div class="aligned-flow-list pl-5">
          <div v-for="(imp, iIdx) in currentImpact" :key="iIdx"
            class="flow-item d-flex align-start mb-1 text-body-2 text-slate-700">
            <span class="dot-prefix text-red-500 mr-2">•</span>
            <span class="step-text">{{ imp }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块 5：现场标准排查规程 (SOP) (统一上下垂直布局，不要左右并排) -->
    <div class="action-card bg-white rounded-xl pa-5 card-shadow mb-4">
      <div class="d-flex align-center justify-space-between mb-3 pb-2 border-b-subtle">
        <div class="d-flex align-center">
          <v-icon color="#ea580c" size="20" class="icon-inline">mdi-clipboard-check-outline</v-icon>
          <h3 class="card-title text-subtitle-1 font-weight-bold text-slate-800 mb-0">现场标准排查规程 (SOP)</h3>
        </div>
      </div>

      <div class="aligned-flow-list pl-5">
        <div v-for="(step, sIdx) in currentOperationSteps" :key="sIdx"
          class="flow-item d-flex align-start mb-2 text-body-2 text-slate-800"
          :class="{ 'mb-0': sIdx === currentOperationSteps.length - 1 }">
          <span class="step-badge">{{ sIdx + 1 }}</span>
          <span class="step-text">{{ cleanSopStep(step) }}</span>
        </div>
      </div>

      <div class="text-caption text-slate-400 pt-2 border-t-subtle mt-3 pl-5">
        注：建议按上述顺序逐项排查现场电源、网线/总线与指示灯状态，确认硬件正常后再尝试复位。
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'
import { getCategoryClass, getCategoryIcon, getCategoryLabel } from '@/views/workbench/components/Tasks/AlarmReportDrawer/category'
import OfflineTelemetryChartCard from './OfflineTelemetryChartCard.vue'
import OfflineSpatialTopologyCard from './OfflineSpatialTopologyCard.vue'
import OfflineHistoryAnalysisCard from './OfflineHistoryAnalysisCard.vue'

defineOptions({
  name: 'OfflineDiagnosisResults'
})

const props = defineProps({
  diagnosis: {
    type: Object,
    default: () => ({})
  },
  analysisId: {
    type: String,
    default: ''
  },
  alarmRecord: {
    type: Object,
    default: () => ({})
  },
  telemetryRecords: {
    type: Array,
    default: () => []
  },
  currentStationAlarms: {
    type: Array,
    default: () => []
  },
  historyAlarms: {
    type: Array,
    default: () => []
  },
  powerTrackingStart: {
    type: String,
    default: ''
  },
  powerTrackingEnd: {
    type: String,
    default: ''
  }
})

const isStationOffline = computed(() => {
  const code = String(props.alarmRecord?.alarmCode || props.alarmRecord?.code || '').trim()
  const name = String(props.alarmRecord?.description || props.alarmRecord?.alarmName || '').trim()
  const dev = String(props.alarmRecord?.equipmentName || props.alarmRecord?.deviceName || '').trim()
  return code === 'A0_00_04_0001' || name.includes('整站') || !dev || dev === '--'
})

const targetStationName = computed(() => {
  return props.alarmRecord?.stationName || props.alarmRecord?.station_name || '储能电站'
})

const targetDeviceName = computed(() => {
  if (isStationOffline.value) {
    return '整站通信'
  }
  const raw = (
    props.alarmRecord?.equipmentName ||
    props.alarmRecord?.equipment_name ||
    props.alarmRecord?.deviceName ||
    props.alarmRecord?.device_name ||
    props.alarmRecord?.name ||
    ''
  ).trim()
  if (!raw || raw === '--') {
    return '本设备'
  }
  return raw
})

const targetDeviceUuid = computed(() => {
  return props.alarmRecord?.uuid || props.alarmRecord?.deviceVid || props.alarmRecord?.equipmentUUID || ''
})

const targetAlarmCode = computed(() => {
  const first = firstDiagnosisItem.value
  return first?.alarmCode || props.alarmRecord?.alarmCode || props.alarmRecord?.code || 'A0_00_04_0002'
})

const targetAlarmCategory = computed(() => {
  const direct = props.alarmRecord?.category || props.alarmRecord?.alarmCategory || firstDiagnosisItem.value?.category
  if (direct) {
    return String(direct).trim().toUpperCase()
  }
  const code = String(targetAlarmCode.value || '').trim()
  const name = String(targetAlarmName.value || '').trim()
  if (code.startsWith('A1_02_') || name.includes('热失控')) {
    return 'SAFETY'
  }
  return 'NORMAL'
})

const targetAlarmName = computed(() => {
  const first = firstDiagnosisItem.value
  return first?.alarmName || props.alarmRecord?.description || (isStationOffline.value ? '整站通信离线告警' : '单机通信离线告警')
})

const targetAlarmTime = computed(() => {
  const t = props.alarmRecord?.alarmTime || props.alarmRecord?.startTime || props.alarmRecord?.lastTime || ''
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : '--'
})

const summaryTitleSuffix = computed(() => {
  return isStationOffline.value ? '整站通信中断' : '单机通信中断'
})

const diagnosisSummary = computed(() => {
  const raw = props.diagnosis?.summary || props.diagnosis?.stationSummary || props.diagnosis?.overview || ''
  let clean = String(raw || '').replace(/^(?:stationSummary|station_summary|summary)\s*[:：]\s*/i, '').trim()
  // 彻底清洗过滤大模型误吐的代码级英文变量与布尔字面量（如 dataInterruption.interrupted=false）
  clean = clean
    .replace(/(?:但|，|且)?\s*dataInterruption\.interrupted\s*=\s*(?:false|true)[，,、\s]*/gi, '，')
    .replace(/(?:但|，|且)?\s*dataInterruption\.[a-zA-Z0-9_]+\s*=\s*[^，,；;\s]+[，,、\s]*/gi, '，')
    .replace(/\bdataInterruption\.[a-zA-Z0-9_.]+\b/gi, '')
    .replace(/\b(?:false|true)\b/gi, '')
    .replace(/，\s*，+/g, '，')
    .replace(/^[,，、\s]+/, '')
  return clean
})

const conclusionsList = computed(() => {
  const d = props.diagnosis || {}
  if (Array.isArray(d.conclusions) && d.conclusions.length > 0) {
    return d.conclusions.map(c => String(c).trim()).filter(Boolean)
  }

  const text = diagnosisSummary.value
  if (!text) return []

  let cleaned = text
    .replace(/^[\s,，;；。]+/, '')
    .replace(/^(?:故障定性研判结论|定性研判结论|研判结论|诊断结论)[:：\s]*/, '')
    .trim()

  let items = cleaned.split(/(?<=[。！？；;])\s*(?=\d+[\.、\s]+|[①②③④⑤⑥⑦⑧⑨⑩]|\n)/)
  if (items.length === 1) {
    const numberedSplit = cleaned.split(/(?:\s+|^)(?:\d+[\.、\s]+|[①②③④⑤⑥⑦⑧⑨⑩])/)
    if (numberedSplit.length > 1) {
      items = numberedSplit.filter(Boolean)
    }
  }

  const res = items
    .map(it => it.trim().replace(/^[\d\.、①②③④⑤⑥⑦⑧⑨⑩\-\s]+/, '').trim())
    .filter(it => it.length > 2)

  return res.length > 0 ? res : [cleaned]
})

const diagnosisItems = computed(() => {
  const d = props.diagnosis || {}
  const raw = d.alarmDiagnoses || d.alarm_diagnoses || d.items || d.list || []
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : [])

  return list.map(item => ({
    ...item,
    alarmCode: item.alarmCode || item.alarm_code || 'A0_00_04_0002',
    alarmName: item.alarmName || item.alarm_name || '单机通信离线告警',
    confirmedFacts: Array.isArray(item.confirmedFacts || item.confirmed_facts) ? (item.confirmedFacts || item.confirmed_facts) : [],
    codeMechanism: Array.isArray(item.codeMechanism || item.code_mechanism) ? (item.codeMechanism || item.code_mechanism) : [],
    possibleCauses: Array.isArray(item.possibleCauses || item.possible_causes) ? (item.possibleCauses || item.possible_causes) : [],
    impact: Array.isArray(item.impact) ? item.impact : [],
    operationSteps: Array.isArray(item.operationSteps || item.operation_steps) ? (item.operationSteps || item.operation_steps) : []
  }))
})

const firstDiagnosisItem = computed(() => {
  return diagnosisItems.value[0] || {}
})

const currentCodeMechanism = computed(() => {
  return firstDiagnosisItem.value.codeMechanism || []
})

const currentPossibleCauses = computed(() => {
  return firstDiagnosisItem.value.possibleCauses || []
})

const currentImpact = computed(() => {
  return firstDiagnosisItem.value.impact || []
})

const currentOperationSteps = computed(() => {
  return firstDiagnosisItem.value.operationSteps || []
})

const categorizedFacts = computed(() => {
  const list = firstDiagnosisItem.value.confirmedFacts || []
  const basicStats = []
  let telemetryFact = ''
  let spatialFact = ''
  let concurrentDevicesFact = ''
  let upstreamFact = ''
  const historyFacts = []

  list.forEach(item => {
    if (!item) return
    const f = String(item)
    if (f.startsWith('时序遥测核验：')) {
      telemetryFact = f.replace('时序遥测核验：', '').trim()
    } else if (f.startsWith('空间拓扑判定：')) {
      spatialFact = f.replace('空间拓扑判定：', '').trim()
    } else if (f.startsWith('同时离线协同设备：')) {
      concurrentDevicesFact = f.replace('同时离线协同设备：', '').trim()
    } else if (f.startsWith('上游导火索溯源：')) {
      upstreamFact = f.replace('上游导火索溯源：', '').trim()
    } else if (f.startsWith('近30天历史画像')) {
      historyFacts.push(f)
    } else if (f.startsWith('告警记录数：') || f.startsWith('受影响设备数：') || f.startsWith('未恢复数量：')) {
      // 单机设备离线告警中：
      // 1. 告警记录数与受影响设备数恒为 1，属于批量聚合模板遗留下来的废话；
      // 2. 原始告警数据根本没有“未恢复数量”字段（只有已恢复/未恢复状态），单条告警下显示“数量：0/1”毫无意义；
      // 3. 频次特征只保留真正有价值的累计触发次数与首次/最近发生时间
      const val = f.split('：')[1]?.trim()
      if (f.startsWith('告警记录数：') || f.startsWith('未恢复数量：') || val === '1') {
        return
      }
      basicStats.push(f)
    } else {
      basicStats.push(f)
    }
  })

  return {
    basicStats,
    telemetryFact,
    spatialFact,
    concurrentDevicesFact,
    upstreamFact,
    historyFacts
  }
})

function cleanSopStep(step) {
  if (!step) return ''
  return String(step).replace(/^\s*(\d+[\.、\s]+)+/, '').trim()
}

function confidenceText(val) {
  const map = { high: '高置信度', medium: '中置信度', low: '低置信度' }
  return map[String(val || '').toLowerCase()] || '中置信度'
}
</script>

<style scoped lang="scss">
.card-shadow {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.border-b-subtle {
  border-bottom: 1px solid #f1f5f9;
}

.border-t-subtle {
  border-top: 1px solid #f1f5f9;
}

.card-title {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #0f172a !important;
  line-height: 1.4;
}

.section-sub-title {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #1e293b !important;
  line-height: 1.4;
}

.alarm-main-title {
  font-size: 20px;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
}

.code-badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 500;
}

.category-pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;

  &.cat-safety {
    background: #fee2e2;
    color: #dc2626;
  }

  &.cat-income {
    background: #fef3c7;
    color: #d97706;
  }

  &.cat-normal {
    background: #eff6ff;
    color: #2563eb;
  }
}

.severity-pill {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fee2e2;
  color: #dc2626;
  font-weight: 600;
}

.icon-inline {
  margin-right: 6px !important;
  vertical-align: text-bottom;
}

.alarm-meta-strip {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 10px 16px !important;
  line-height: 1.5;
}

.prop-item {
  line-height: 1.4;
}

.verdict-banner-pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 6px;
  white-space: nowrap;

  &.tag-triggered {
    background: #fef2f2;
    color: #b91c1c;
  }
}

.badge-sub {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 500;
}

/* 研判结论高亮横幅 (四周呼吸感充足，padding 18px 22px，绝不顶死) */
.verdict-highlight-banner {
  background: #f0f7ff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  padding: 18px 22px !important;
  margin-top: 14px !important;
  line-height: 1.7;
  box-sizing: border-box;
}

.verdict-banner-header {
  line-height: 1;
}

.verdict-banner-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
  background: #dbeafe;
  padding: 3px 8px;
  border-radius: 4px;
  line-height: 1.35;
}

.verdict-banner-text {
  font-size: 14px;
  line-height: 1.7;
  color: #1e293b;
}

.fact-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 14px;
    color: #334155;
    line-height: 1.65;

    &::before {
      content: '';
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: #64748b;
      margin-top: 8px;
      flex-shrink: 0;
    }
  }
}

.evidence-upstream-box {
  background: transparent;
  border: none;
  padding: 0;
}

.mechanism-box {
  .mechanism-text {
    line-height: 1.65;
  }
}

.aligned-flow-list {
  display: flex;
  flex-direction: column;
}

.flow-item {
  line-height: 1.65;

  .step-badge {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #dbeafe;
    color: #1d4ed8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 2px;
    margin-right: 10px !important;
  }

  .dot-prefix {
    width: 14px;
    text-align: center;
    font-size: 16px;
    line-height: 1.4;
    flex-shrink: 0;
  }

  .step-text {
    flex: 1;
    min-width: 0;
    font-size: 14px;
  }

  /* 主题蓝色统一置信度标签 (全用主题蓝，不同置信度只改背景色透明度，绝不改字体色) */
  .confidence-tag {
    flex-shrink: 0;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    margin-top: 2px;
    line-height: 1.4;
    color: #2563eb !important;
    /* 字体色全局固定为主题蓝，绝不改字体色 */
    font-weight: 500;
    transition: all 0.2s ease;

    &.confidence-high {
      background: rgba(37, 99, 235, 0.16);
      border: 1px solid rgba(37, 99, 235, 0.32);
    }

    &.confidence-medium {
      background: rgba(37, 99, 235, 0.08);
      border: 1px solid rgba(37, 99, 235, 0.18);
    }

    &.confidence-low {
      background: rgba(37, 99, 235, 0.03);
      border: 1px solid rgba(37, 99, 235, 0.10);
    }
  }
}

.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
</style>
